import { date } from '@ipfabric/utils';
import { exec } from '@ipfabric/utils/fp/function';
import { min } from '@ipfabric/utils/fp/number';
import { pipe } from 'fp-ts/lib/function';

export interface RunningAnimation {
  afterFrame: (cb: () => void) => void;
  cancel: () => void;
  forceFinish: () => void;
  getElapsedTime: () => number;
  getPhase: () => number;
  readonly promise: Promise<void>;
}

export type AnimationFn = (
  lengthMilliseconds: number,
  apply: (phase: number) => void,
) => RunningAnimation;

export interface AnimationController {
  animate: AnimationFn;
  cancelAll: () => void;
}

export function createAnimationController(
  requestRender: (cb: () => void) => Promise<void>,
): AnimationController {
  const cancellers = new Set<() => void>();
  const afterFrame: (() => void)[] = [];
  return {
    animate: (lengthMilliseconds, apply) => {
      let cancelled = false;
      let forceFinished = false;
      const startTime = date.getTs();
      const endAt = startTime + lengthMilliseconds;
      let currentTime: number = startTime;
      let phase = 0;
      const getElapsedTime = () => currentTime - startTime;
      const getPhase = () => phase;
      const updatePhase = () => {
        phase = pipe(getElapsedTime() / lengthMilliseconds, min(1));
      };
      const cancel = () => {
        cancelled = true;
      };
      const forceFinish = () => {
        forceFinished = true;
      };
      cancellers.add(cancel);
      const handleAfterFrame = () => {
        afterFrame.forEach(exec);
        afterFrame.splice(0);
      };

      return {
        afterFrame: afterFrame.push.bind(afterFrame),
        cancel,
        forceFinish,
        getElapsedTime,
        getPhase,
        promise: exec(async () => {
          if (lengthMilliseconds === 0) {
            // short-circuit zero-length animations to avoid zero division
            await requestRender(() => {
              if (!cancelled) {
                phase = 1;
                apply(1);
              }
            });
            handleAfterFrame();
          } else {
            const step = () => {
              if (!cancelled) {
                currentTime = Math.min(date.getTs(), endAt);
                if (forceFinished) {
                  phase = 1;
                  cancelled = true;
                } else {
                  updatePhase();
                }
                apply(phase);
              }
            };
            do {
              await requestRender(step);
              handleAfterFrame();
            } while (!cancelled && lengthMilliseconds > getElapsedTime());
          }
          cancellers.delete(cancel);
        }),
      };
    },
    cancelAll: () => {
      cancellers.forEach(exec);
    },
  };
}
